import { writeModalParam, clearModalParam, currentModalParam } from '@theme/modal-deeplink';

const MODAL_ID = 'reviews';

const STAR_PATH =
  'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

const SHIELD_PATH =
  'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z';

const REPLY_ARROW_PATH =
  'M9 18l-6-6 6-6v4h6c3.31 0 6 2.69 6 6v6h-2v-6c0-2.21-1.79-4-4-4H9v4z';

const POLL_INTERVAL_MS = 150;
const POLL_TIMEOUT_MS = 15000;

const lang = document.documentElement.lang || 'en';
const numberFormatter = new Intl.NumberFormat(lang);
const dateFormatter = new Intl.DateTimeFormat(lang, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return dateFormatter.format(d);
}

function pluralize(count, one, other) {
  return (count === 1 ? one : other).replace('{count}', numberFormatter.format(count));
}

function fractionalStars(rating, sizeClass) {
  let html = '';
  for (let i = 1; i <= 5; i++) {
    const fillPct = Math.max(0, Math.min(1, rating - (i - 1))) * 100;
    html += `<span class="cr-star ${sizeClass}" aria-hidden="true"><svg class="cr-star__svg cr-star__svg--empty" viewBox="0 0 32 32"><path d="${STAR_PATH}"/></svg><svg class="cr-star__svg cr-star__svg--fill" viewBox="0 0 32 32" style="--fill-pct: ${fillPct}%"><path d="${STAR_PATH}"/></svg></span>`;
  }
  return html;
}

// Vulgarity filter -----------------------------------------------------------
//
// Hides a review from the storefront when its title or body contains profanity
// or a slur. This is a DISPLAY-SIDE BACKSTOP only — the review still exists in
// Judge.me and should be hidden/archived there properly; nothing here changes
// the review count Judge.me reports.
//
// Deliberately NOT on this list, and it should stay that way:
//   * "hell" — non-negotiable. "A sobering book on heaven and hell" is an
//     ordinary sentence in a review of these publishers' books, so blocking it
//     would hide legitimate reviews constantly. Every other borderline word is
//     rare enough in a customer review to be worth blocking;
//   * minced oaths ("dang", "darn", "heck", "gosh") — nobody balks at those;
//   * "God" and "Jesus" — a reverent use and an exclamatory one are
//     indistinguishable by regex, and on a Christian publisher's storefront the
//     reverent use overwhelmingly dominates. Blocking either would be a
//     catastrophe; "goddamn" is listed instead, which is unambiguous;
//   * insults that are just harsh criticism ("stupid", "idiot", "moron") —
//     unpleasant, but suppressing them is suppressing negative reviews.
// The bar is deliberately strict: a shopper's grandma should be able to read
// any review on the page without wincing. The cost is that a review quoting
// Balaam's ass, the whore of Babylon, or kicking against the pricks is hidden
// along with the vulgar uses — accepted, since those phrasings are vanishingly
// rare in a customer review.
// A merchant who wants any of those blocked can add them per-block via the
// "Additional blocked words" setting rather than editing this list.
const PROFANITY_STEMS = [
  'fuck',
  'motherfuck',
  'clusterfuck',
  'shit',
  'bullshit',
  'horseshit',
  'dogshit',
  'shithead',
  'bitch',
  'cunt',
  'asshole',
  'arsehole',
  'dumbass',
  'smartass',
  'cocksuck',
  'dickhead',
  'pussy',
  'twat',
  'wank',
  'bollock',
  'slut',
  'skank',
  'douche',
  'turd',
  'jizz',
  'blowjob',
  'dildo',
  'goddamn',
  'godamn',
  'faggot',
  'nigger',
  'nigga',
  'kike',
  'wetback',
  'gook',
  'tranny',
  // Crude rather than obscene, but they fail the "would grandma balk" bar.
  'crap',
  'suck',
  'piss',
  'bastard',
  'whore',
  'bugger',
  'porn',
  'wtf',
  'stfu',
  'lmfao',
];

// Stems where the three-letter suffix allowance over-reaches into real words
// ("retardant"), so only exact inflections count. Kept out of the loose list
// rather than dropped, because they are still slurs worth blocking.
// Each of these would collide in the loose tier: retard->"retardant",
// arse->"arsenal", fart->"farther", prick->"prickly", ass->"assure"/"assist".
// Exact inflections only. "damn" sits here for a second reason — it keeps
// "damnation" and "damnable" (5+ trailing letters) clean, which is exactly the
// line this publisher needs: the oath is blocked, the doctrine is not.
const PROFANITY_STRICT_STEMS = [
  'retard',
  'fag',
  'ass',
  'damn',
  'prick',
  'arse',
  'fart',
  'boob',
];

// Common character substitutions, so "sh1t" / "a$$hole" / "f@ggot" still match.
const LEET_MAP = {
  '@': 'a',
  '4': 'a',
  '8': 'b',
  '3': 'e',
  '1': 'i',
  '!': 'i',
  '|': 'i',
  '0': 'o',
  $: 's',
  '5': 's',
  '7': 't',
  '+': 't',
};

// Strips markup and accents, folds leet substitutions, collapses padded repeats
// ("fuuuuck" -> "fuck") and reduces every run of punctuation to one space, so
// matching runs against plain lowercase words.
function normalizeForProfanity(value) {
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[@48310!|$57+]/g, (char) => LEET_MAP[char] || char)
    .replace(/(.)\1{2,}/g, '$1')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(?:[a-z] ){2,}[a-z]\b/g, (run) => run.replace(/ /g, ''))
    .trim();
}

// One regex per block. Stems match at a word boundary plus up to three trailing
// letters, which covers ordinary inflections ("-s", "-ed", "-ing", "-er")
// without reaching into longer unrelated words — the leading \b is what keeps
// "classic", "assassin" and "Scunthorpe" out of it.
function buildProfanityMatcher(enabled, extraWords) {
  if (!enabled) return null;
  const extra = String(extraWords || '')
    .split(',')
    .map((word) => normalizeForProfanity(word))
    .filter(Boolean);
  const escape = (stem) => stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const loose = PROFANITY_STEMS.concat(extra).map(escape);
  const strict = PROFANITY_STRICT_STEMS.map(escape);
  return new RegExp(
    `\\b(?:${loose.join('|')})[a-z]{0,3}\\b|\\b(?:${strict.join('|')})(?:s|es|ed|ing)?\\b`
  );
}

function reviewIsClean(review, matcher) {
  if (!matcher) return true;
  const text = normalizeForProfanity(
    `${review.title || ''} ${review.body || review.body_html || ''}`
  );
  return !matcher.test(text);
}

// Judge.me's payload has NO `total_reviews` key — the real total is
// `number_of_reviews`. Reading the wrong one silently fell back to the inlined
// page size (5), which under-reported the count and, because `total` then
// equalled the rendered slice, suppressed the "See all reviews" button outright.
function reviewTotal(data) {
  const reviews = Array.isArray(data.reviews) ? data.reviews : [];
  return Number(data.number_of_reviews || data.total_reviews || reviews.length || 0);
}

// jdgm.data only inlines the FIRST page of reviews (pagination.per_page, 5 by
// default). The rest come from Judge.me's public, token-less, CORS-open widget
// CDN endpoint — the same one the native widget uses — which returns review
// objects in the exact shape renderCard already handles. Undocumented and
// reverse-engineered, so every consumer degrades to the inlined page on error.
const MAX_FETCHED_PAGES = 20;

function buildReviewsUrl(shopDomain, productId, page) {
  const url = new URL('https://cdn.judge.me/reviews/reviews_for_widget');
  url.searchParams.set('product_id', productId);
  url.searchParams.set('page', String(page));
  url.searchParams.set('sort_by', 'created_at');
  url.searchParams.set('sort_dir', 'desc');
  url.searchParams.set('skip_other_languages', 'true');
  url.searchParams.set('widget_theme', 'standard');
  url.searchParams.set('shop_domain', shopDomain);
  url.searchParams.set('platform', 'shopify');
  return url.toString();
}

async function fetchReviewsPage(shopDomain, productId, page) {
  const res = await fetch(buildReviewsUrl(shopDomain, productId, page), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Judge.me request failed: ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.reviews) ? json.reviews : [];
}

function getWidgetData(productId) {
  const reviewWidget = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget;
  if (!reviewWidget) return null;
  return reviewWidget[productId] || reviewWidget[String(productId)] || null;
}

function waitForWidgetData(productId) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const data = getWidgetData(productId);
      if (data) return resolve({ status: 'data', data });

      const elapsed = Date.now() - start;
      const judgemeReady = document.querySelector(
        '.jdgm-widget.jdgm-review-widget.jdgm--done-setup-widget'
      );
      // Judge.me's widget has finished setup but didn't inline data for
      // this product — that's the "0 reviews" case, not an error.
      if (judgemeReady && elapsed > 3000) return resolve({ status: 'empty' });

      if (elapsed > POLL_TIMEOUT_MS) return resolve({ status: 'error' });
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
  });
}

function findJudgemeWriteTrigger(host) {
  const local = host.querySelector('.jdgm-write-rev-link');
  if (local) return local;
  const selectors = [
    '.jdgm-write-rev-link',
    '.jm-review-widget-minimal-header__action-buttons .jm-button--primary',
    '.jm-action-buttons__button.jm-button--primary',
    '[data-jdgm-action="write-review"]',
  ];
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) return el;
  }
  return null;
}

function renderSummary(host, data, labels, classes) {
  const root = host.querySelector('[data-cr-summary]');
  if (!root) return;

  const avg = Number(data.average_rating || 0);
  const total = reviewTotal(data);

  if (!total) {
    root.innerHTML = `<p class="customer-reviews__empty">${escapeHtml(labels.empty)}</p>`;
    return;
  }

  const histogram = Array.isArray(data.histogram) ? data.histogram : [];
  const byRating = new Map(histogram.map((h) => [Number(h.rating), h]));

  const ratingLine = `<div class="cr-rating-line"><span class="cr-rating-line__stars">${fractionalStars(avg, 'cr-star--md')}</span><span class="cr-rating-line__value ${escapeHtml(classes.rating || '')}">${escapeHtml(avg.toFixed(1))} ${escapeHtml(labels.out_of)}</span></div>`;
  const countLine = `<p class="cr-count">${escapeHtml(pluralize(total, labels.reviews_one, labels.reviews_other))}</p>`;
  const rows = [5, 4, 3, 2, 1]
    .map((rating) => {
      const bucket = byRating.get(rating) || { percentage: 0 };
      const pct = Math.round(Number(bucket.percentage || 0));
      const rowLabel = (labels.stars_row || '{count} stars').replace('{count}', rating);
      return `<div class="cr-histogram__row"><span class="cr-histogram__label">${escapeHtml(rowLabel)}</span><div class="cr-histogram__bar" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100"><div class="cr-histogram__bar-fill" style="--pct: ${pct}%"></div></div><span class="cr-histogram__pct">${pct}%</span></div>`;
    })
    .join('');

  root.innerHTML = `${ratingLine}${countLine}<div class="cr-histogram">${rows}</div>`;
}

function renderVerified(review, labels) {
  if (!review.verified_buyer) return '';
  return `<span class="cr-card__verified"><svg class="cr-card__verified-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="${SHIELD_PATH}"/></svg><span>${escapeHtml(labels.verified)}</span></span>`;
}

function renderAvatar(review) {
  if (review.avatar_image_url) {
    return `<div class="cr-card__avatar"><img src="${escapeHtml(review.avatar_image_url)}" alt="" loading="lazy"></div>`;
  }
  return `<div class="cr-card__avatar" aria-hidden="true"></div>`;
}

// Judge.me picture shapes differ between the inlined widget data and the CDN
// widget endpoint: `pictures_urls` can hold plain URL strings, size-keyed
// objects ({ compact, small, huge, original }), or objects nesting those under
// `urls`; the documented API uses `pictures` with { hidden, urls: {...} }.
// Rendering a size-keyed object straight into src stringifies it to
// "[object Object]" and every review photo paints as a broken image — normalize
// all the shapes to { thumb, full } instead.
const PICTURE_THUMB_KEYS = ['compact', 'small', 'huge', 'original'];
const PICTURE_FULL_KEYS = ['huge', 'original', 'compact', 'small'];

function pickPictureUrl(source, keys) {
  if (!source || typeof source !== 'object') return '';
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return '';
}

// Last resort for a shape whose size keys aren't named any of the above: take
// the first value that actually looks like a URL, so an unanticipated key name
// still paints a photo instead of silently rendering nothing.
function firstUrlValue(source) {
  if (!source || typeof source !== 'object') return '';
  for (const value of Object.values(source)) {
    if (typeof value === 'string' && /^(https?:)?\/\//.test(value)) return value;
  }
  return '';
}

function normalizePicture(picture) {
  if (typeof picture === 'string') return picture ? { thumb: picture, full: picture } : null;
  if (!picture || typeof picture !== 'object' || picture.hidden) return null;
  const urls = picture.urls && typeof picture.urls === 'object' ? picture.urls : picture;
  const thumb = pickPictureUrl(urls, PICTURE_THUMB_KEYS) || firstUrlValue(urls);
  const full = pickPictureUrl(urls, PICTURE_FULL_KEYS) || firstUrlValue(urls);
  if (!thumb && !full) return null;
  return { thumb: thumb || full, full: full || thumb };
}

function reviewPictures(review) {
  const raw = Array.isArray(review.pictures_urls)
    ? review.pictures_urls
    : Array.isArray(review.pictures)
      ? review.pictures
      : [];
  return raw.map(normalizePicture).filter(Boolean);
}

function renderPhotos(review, labels) {
  const pictures = reviewPictures(review);
  if (!pictures.length) return '';
  const zoomLabel = escapeHtml((labels && labels.zoom) || 'Zoom');
  return `<div class="cr-card__photos">${pictures
    .map(
      (picture) =>
        `<button type="button" class="cr-card__photo" data-cr-photo data-full-src="${escapeHtml(picture.full)}" aria-label="${zoomLabel}"><img src="${escapeHtml(picture.thumb)}" alt="" loading="lazy"></button>`
    )
    .join('')}</div>`;
}

// Review photos open in a lightbox instead of a new tab. One dialog per block,
// rendered as a sibling of the all-reviews modal, so this single wiring serves
// both the on-page preview feed and the modal feed (both live inside `host`).
// The dialog reuses base.css's `.dialog-modal` chrome — backdrop, open
// animation, full-screen mobile treatment — rather than inventing modal styling.
function wireLightbox(host, labels) {
  const dialog = host.querySelector('[data-cr-lightbox]');
  const image = dialog && dialog.querySelector('[data-cr-lightbox-image]');
  if (!dialog || !image) return;

  const closeButton = dialog.querySelector('[data-cr-lightbox-close]');
  let lockedByUs = false;

  // Every exit path — the close button, a backdrop click, and Escape's native
  // `cancel` — ends in the dialog's own `close` event, so the cleanup lives
  // here once and can't be skipped by a path that bypasses our handlers.
  dialog.addEventListener('close', () => {
    if (lockedByUs) {
      document.documentElement.removeAttribute('scroll-lock');
      lockedByUs = false;
    }
    image.removeAttribute('src');
  });

  const close = () => {
    if (dialog.open) dialog.close();
  };

  host.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-cr-photo]');
    if (!trigger || !host.contains(trigger)) return;

    const src = trigger.dataset.fullSrc;
    if (!src || dialog.open) return;

    image.src = src;
    image.alt = (labels && labels.zoom) || '';

    // Opened from inside the all-reviews modal the page is already locked by
    // that dialog. `html[scroll-lock]` is a plain `overflow: hidden` that never
    // touches the saved scroll offset, so it composes with that lock instead of
    // clobbering it — and we only clear what we set.
    if (!document.documentElement.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      lockedByUs = true;
    }

    dialog.showModal();
    window.publishStorefrontEvent?.('review_photo_opened', { placement: 'product_page' });
  });

  if (closeButton) closeButton.addEventListener('click', close);

  // Backdrop click. The dialog is a sibling of the reviews modal, so this
  // never reaches that modal's own outside-click handler.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });
}

function renderReply(review, replyShop) {
  const reply = review.shop_reply || review.reply;
  if (!reply) return '';
  const body = reply.body_html || (reply.body ? `<p>${escapeHtml(reply.body)}</p>` : '');
  const shopName = reply.shop_name || replyShop.name || '';
  const logo = replyShop.logo
    ? `<span class="cr-card__reply-logo"><img src="${escapeHtml(replyShop.logo)}" alt="" loading="lazy"></span>`
    : `<span class="cr-card__reply-logo" aria-hidden="true"></span>`;
  return `<div class="cr-card__reply"><svg class="cr-card__reply-arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="${REPLY_ARROW_PATH}"/></svg><div><div class="cr-card__reply-author">${logo}<span>${escapeHtml(shopName)}</span></div><div class="cr-card__reply-body">${body}</div></div></div>`;
}

function renderCard(review, labels, classes, replyShop) {
  const name = review.is_anonymous_reviewer ? labels.anonymous : review.reviewer_name || labels.anonymous;
  const title = review.title
    ? `<span class="cr-card__title ${escapeHtml(classes.title || '')}">${escapeHtml(review.title)}</span>`
    : '';
  const body = review.body_html || (review.body ? `<p>${escapeHtml(review.body)}</p>` : '');
  return `
    <article class="cr-card">
      <div class="cr-card__head">
        <div class="cr-card__author">
          ${renderAvatar(review)}
          <div class="cr-card__author-text">
            <span class="cr-card__name ${escapeHtml(classes.name || '')}">${escapeHtml(name)}</span>
            <time class="cr-card__date" datetime="${escapeHtml(review.created_at || '')}">${escapeHtml(formatDate(review.created_at))}</time>
          </div>
        </div>
        ${renderVerified(review, labels)}
      </div>
      <div class="cr-card__rating-row">
        <span class="cr-card__stars" aria-label="${escapeHtml(review.rating)} out of 5">${fractionalStars(review.rating, 'cr-star--sm')}</span>
        ${title}
      </div>
      <div class="cr-card__body ${escapeHtml(classes.body || '')}">${body}</div>
      ${renderPhotos(review, labels)}
      ${renderReply(review, replyShop)}
    </article>
  `;
}

function renderFeed(host, data, labels, classes, replyShop, options) {
  const root = host.querySelector('[data-cr-feed]');
  if (!root) return;
  const reviews = (Array.isArray(data.reviews) ? data.reviews : []).filter((review) =>
    reviewIsClean(review, options.profanity_matcher)
  );
  const total = reviewTotal(data);
  if (!reviews.length) {
    root.innerHTML = '';
    return;
  }
  const visible = reviews.slice(0, options.preview_count);
  const cards = visible.map((r) => renderCard(r, labels, classes, replyShop)).join('');
  const seeAll =
    total > visible.length
      ? `<button type="button" class="customer-reviews__see-all button button-secondary" data-cr-see-all>${escapeHtml(
          (labels.see_all || 'See all {count} reviews').replace('{count}', numberFormatter.format(total))
        )}</button>`
      : '';
  root.innerHTML = `${cards}${seeAll}`;
}

function wireWriteButton(host) {
  const btn = host.querySelector('[data-cr-write-trigger]');
  if (!btn) return;
  btn.addEventListener('click', () => {
    window.publishStorefrontEvent?.('review_write_clicked', { placement: 'product_page' });
    const trigger = findJudgemeWriteTrigger(host);
    if (trigger) trigger.click();
  });
}

function wireDialog(host, data, labels, classes, replyShop, options) {
  const dialog = host.querySelector('[data-cr-dialog]');
  if (!dialog) return;
  const body = dialog.querySelector('[data-cr-dialog-body]');
  const closeBtn = dialog.querySelector('[data-cr-dialog-close]');

  const renderBody = () => {
    const reviews = (Array.isArray(data.reviews) ? data.reviews : []).filter((review) =>
      reviewIsClean(review, options.profanity_matcher)
    );
    body.innerHTML = reviews.map((r) => renderCard(r, labels, classes, replyShop)).join('');
  };

  // Page 1 is already inlined; pull the rest once, on first open, so the modal
  // shows every review instead of only the inlined page.
  const loadRemainingPages = async () => {
    const pagination = data.pagination || {};
    const totalPages = Math.min(Number(pagination.total_pages) || 1, MAX_FETCHED_PAGES);
    if (totalPages < 2 || !options.shop_domain || !options.product_id) return;

    for (let page = 2; page <= totalPages; page++) {
      let reviews;
      try {
        reviews = await fetchReviewsPage(options.shop_domain, options.product_id, page);
      } catch (error) {
        // Undocumented endpoint: if it moves or fails, keep what is already
        // rendered rather than blanking the modal.
        break;
      }
      if (!reviews.length) break;
      const clean = reviews.filter((review) => reviewIsClean(review, options.profanity_matcher));
      if (!clean.length) continue;
      body.insertAdjacentHTML(
        'beforeend',
        clean.map((r) => renderCard(r, labels, classes, replyShop)).join('')
      );
    }
  };

  const open = () => {
    if (!body.dataset.rendered) {
      renderBody();
      body.dataset.rendered = 'true';
      loadRemainingPages();
    }
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    window.publishStorefrontEvent?.('reviews_opened', { placement: 'product_page' });
    writeModalParam(MODAL_ID);
  };
  const close = () => {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    clearModalParam(MODAL_ID);
  };

  host.addEventListener('click', (event) => {
    if (event.target.closest('[data-cr-see-all]')) {
      event.preventDefault();
      open();
    }
  });
  if (closeBtn) closeBtn.addEventListener('click', close);
  // The native dialog's own close (Esc / backdrop via ::backdrop) also fires
  // a `close` event — keep the URL in sync for those paths too.
  dialog.addEventListener('close', () => clearModalParam(MODAL_ID));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  if (currentModalParam() === MODAL_ID) open();
}

async function hydrate(host) {
  const productId = host.dataset.productId;

  const labels = {
    out_of: host.dataset.labelOutOf || 'out of 5.0',
    reviews_one: host.dataset.labelReviewsOne || '{count} review',
    reviews_other: host.dataset.labelReviewsOther || '{count} reviews',
    stars_row: host.dataset.labelStarsRow || '{count} stars',
    verified: host.dataset.labelVerified || 'Verified',
    zoom: host.dataset.labelZoom || 'Zoom',
    anonymous: host.dataset.labelAnonymous || 'Anonymous',
    see_all: host.dataset.labelSeeAll || 'See all {count} reviews',
    empty: host.dataset.labelEmpty || 'No reviews yet.',
    error: host.dataset.labelError || 'Reviews could not be loaded.',
  };
  const classes = {
    rating: host.dataset.ratingClass || '',
    name: host.dataset.nameClass || '',
    title: host.dataset.titleClass || '',
    body: host.dataset.bodyClass || '',
  };
  const replyShop = {
    name: host.dataset.shopReplyName || '',
    logo: host.dataset.shopReplyLogo || '',
  };
  const options = {
    preview_count: Number(host.dataset.previewCount) || 5,
    shop_domain: host.dataset.shopDomain || '',
    product_id: productId,
    // Absent dataset (a template saved before this setting existed) leaves the
    // filter ON — only an explicit "false" turns it off.
    profanity_matcher: buildProfanityMatcher(
      host.dataset.profanityFilter !== 'false',
      host.dataset.profanityWords
    ),
  };

  wireWriteButton(host);
  wireLightbox(host, labels);

  if (!productId) {
    host.setAttribute('data-state', 'error');
    return;
  }

  host.setAttribute('data-state', 'loading');

  const result = await waitForWidgetData(productId);

  if (result.status === 'error') {
    host.setAttribute('data-state', 'error');
    const summary = host.querySelector('[data-cr-summary]');
    if (summary)
      summary.innerHTML = `<p class="customer-reviews__error">${escapeHtml(labels.error)}</p>`;
    return;
  }

  if (result.status === 'empty') {
    host.setAttribute('data-state', 'empty');
    renderSummary(host, { average_rating: 0, total_reviews: 0, histogram: [] }, labels, classes);
    return;
  }

  host.setAttribute('data-state', 'loaded');
  renderSummary(host, result.data, labels, classes);
  renderFeed(host, result.data, labels, classes, replyShop, options);
  wireDialog(host, result.data, labels, classes, replyShop, options);
}

function init() {
  document.querySelectorAll('[data-customer-reviews]').forEach(hydrate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
